/**
 * BI Command Center — Main page with tabbed navigation
 *
 * 18 sub-tabs grouped into 6 categories (Data / Modeling / Inference /
 * Lifecycle / Trust / Billing) per WQ-253 restructure. Sub-tab content
 * resolved through `biCategoryRegistry` so adding a new tab is a
 * single-line registry change.
 *
 * Phase 4.8-B — WQ-198 (Tier 1/2 tabs) + WQ-200 (Tier 3/5 tabs)
 * Phase 4.10 β5 — WQ-240 — Run-action launcher buttons (DQ / Fairness / Explainability)
 * Phase 4.10 β6 — WQ-241 — Monte Carlo wizard launcher
 * Phase 4.10 polish — WQ-253 — 6-category two-row navigation; URL `?tab=` back-compat preserved
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnalyticsIcon } from '../components/common/Icons';
import BiCommandCenterBanner from '../components/bi/BiCommandCenterBanner';
import { BiPanelErrorBoundary } from '../components/bi/BiPanelErrorBoundary';
import BICommandCenterCategoryNav from '../components/bi/BICommandCenterCategoryNav';
import {
  BI_CC_CATEGORIES,
  findCategoryForSubTab,
  defaultSubTabForCategory,
  DEFAULT_BI_CATEGORY,
  DEFAULT_BI_SUB_TAB,
} from '../components/bi/biCategoryRegistry';
// Modals + special panel mounted alongside the registry-driven panel for
// the 4 launcher tabs (monte-carlo, fairness, explainability, data-quality)
// and Monte Carlo's refresh-key remount pattern.
import MonteCarloWizard from '../components/bi/MonteCarloWizard';
import MonteCarloPanel from '../components/bi/MonteCarloPanel';
import FairnessLauncher from '../components/bi/FairnessLauncher';
import ExplainabilityLauncher from '../components/bi/ExplainabilityLauncher';
import DataQualityLauncher from '../components/bi/DataQualityLauncher';

interface BICommandCenterProps {
  defaultTab?: string;
}

// WQ-240: button style shared by the four launcher triggers
const launcherButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--accent-primary)',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: 12,
};

const BICommandCenterPage: React.FC<BICommandCenterProps> = ({ defaultTab }) => {
  // URL ?tab= back-compat (WQ-253) — preserve flat sub-tab key shape so
  // existing operator bookmarks continue to resolve. Reverse-lookup via
  // registry chooses the category at mount time.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  const initialSubTab = tabFromUrl ?? defaultTab ?? DEFAULT_BI_SUB_TAB;
  const initialCategory =
    findCategoryForSubTab(initialSubTab)?.key ?? DEFAULT_BI_CATEGORY;

  const [activeCategory, setActiveCategory] = useState<string>(initialCategory);
  const [activeSubTab, setActiveSubTab] = useState<string>(initialSubTab);

  // WQ-240 launcher modal state
  const [dqOpen, setDqOpen] = useState(false);
  const [fairnessOpen, setFairnessOpen] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  // WQ-241 Monte Carlo wizard state
  const [mcWizardOpen, setMcWizardOpen] = useState(false);
  const [mcRefreshNonce, setMcRefreshNonce] = useState(0);

  const handleCategoryChange = (categoryKey: string) => {
    const defaultSub = defaultSubTabForCategory(categoryKey);
    setActiveCategory(categoryKey);
    if (defaultSub) {
      setActiveSubTab(defaultSub);
      setSearchParams({ tab: defaultSub }, { replace: true });
    }
  };

  const handleSubTabChange = (subTabKey: string) => {
    setActiveSubTab(subTabKey);
    setSearchParams({ tab: subTabKey }, { replace: true });
  };

  // Sync URL → state for back/forward navigation
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeSubTab) {
      const cat = findCategoryForSubTab(tabFromUrl);
      if (cat) {
        setActiveCategory(cat.key);
        setActiveSubTab(tabFromUrl);
      }
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSubTab = useMemo(() => {
    const cat = BI_CC_CATEGORIES.find((c) => c.key === activeCategory);
    return cat?.subTabs.find((s) => s.key === activeSubTab);
  }, [activeCategory, activeSubTab]);

  const ActiveComponent = currentSubTab?.component;

  return (
    <div style={{ padding: '0 16px' }} data-testid="bi-command-center-page">
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AnalyticsIcon size={24} />
          BI Command Center
        </h2>
      </div>

      {/* WQ-226: Contextual framing — platform-operator scope, read-only in 4.8, workflow in 4.10 */}
      <BiCommandCenterBanner />

      {/* WQ-253: 6-category two-row navigation */}
      <BICommandCenterCategoryNav
        activeCategory={activeCategory}
        activeSubTab={activeSubTab}
        onCategoryChange={handleCategoryChange}
        onSubTabChange={handleSubTabChange}
      />

      {/* Action buttons for the 4 launcher sub-tabs (keyed on activeSubTab) */}
      {activeSubTab === 'monte-carlo' && (
        <button
          data-testid="bi-cc-monte-carlo-run-button"
          onClick={() => setMcWizardOpen(true)}
          style={launcherButtonStyle}
        >
          + Run Simulation
        </button>
      )}
      {activeSubTab === 'fairness' && (
        <button
          data-testid="bi-cc-fairness-run-button"
          onClick={() => setFairnessOpen(true)}
          style={launcherButtonStyle}
        >
          + Run Fairness Audit
        </button>
      )}
      {activeSubTab === 'explainability' && (
        <button
          data-testid="bi-cc-explainability-run-button"
          onClick={() => setExplainOpen(true)}
          style={launcherButtonStyle}
        >
          + Run Explainability
        </button>
      )}
      {activeSubTab === 'data-quality' && (
        <button
          data-testid="bi-cc-data-quality-run-button"
          onClick={() => setDqOpen(true)}
          style={launcherButtonStyle}
        >
          + Run Quality Check
        </button>
      )}

      {/* Active sub-tab panel — registry-driven render with two specials:
          monte-carlo gets a refresh key to force remount on wizard submit;
          everything else renders the registry component as-is. */}
      {activeSubTab === 'monte-carlo' ? (
        <BiPanelErrorBoundary panelName="Monte Carlo">
          <MonteCarloPanel key={mcRefreshNonce} />
        </BiPanelErrorBoundary>
      ) : ActiveComponent ? (
        <BiPanelErrorBoundary panelName={currentSubTab?.label ?? 'Unknown'}>
          <ActiveComponent />
        </BiPanelErrorBoundary>
      ) : null}

      {/* Modals — always mounted; isOpen controls visibility */}
      <MonteCarloWizard
        isOpen={mcWizardOpen}
        onClose={() => setMcWizardOpen(false)}
        onSubmitted={() => setMcRefreshNonce((n) => n + 1)}
      />
      <FairnessLauncher isOpen={fairnessOpen} onClose={() => setFairnessOpen(false)} />
      <ExplainabilityLauncher
        isOpen={explainOpen}
        onClose={() => setExplainOpen(false)}
      />
      <DataQualityLauncher isOpen={dqOpen} onClose={() => setDqOpen(false)} />
    </div>
  );
};

export default BICommandCenterPage;
